import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  withLatestFrom,
} from 'rxjs';
import { CreateValueRequest } from '../../api/core/models/api/create-value-request';
import { UpdateValueRequest } from '../../api/core/models/api/update-value-request';
import { APP_ROUTE_PATHS, EDITOR_ROUTE_PATHS } from '../../app.routes.constant';
import { ClientBoard } from '../../model/client-model/client-board';
import { ClientVariable } from '../../model/client-model/client-variable';
import { BackendInventoryService } from '../../services/backend/backend-inventory.service';
import { ErrorType } from '../../services/error-handler.service';
import { InventorySaveManagerService } from '../../services/store/inventories/inventory-save-manager.service';
import { selectBoard } from '../board/board.selectors';
import { selectRouteBoardId, selectRouteOrganisationId } from '../router/router.selectors';
import { CustomRouterNavigationAction } from '../router/router.types';
import { inventoriesEffectsActions, inventoriesFacadeActions } from './inventories.actions';
import {
  selectInventories,
  selectMonetaryVariableIds,
  selectVariablesQueuedForSave,
  selectVariablesSaving,
} from './inventories.selectors';
import { Variable } from './inventories.types';
import { createVariableSaveQueueId } from './utils/create-variable-save-queue-id';
import { MultiyearUpdateValuesService } from './utils/multiyear-update-values.service';

const pathsRequiringInventories = [
  `${EDITOR_ROUTE_PATHS.HOME.ROOT}/${EDITOR_ROUTE_PATHS.HOME.CHILDREN.PROGRESS_OVERVIEW}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.EXPORT_IMPORT}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.RESOURCE_BALANCE}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.ECONOMICS}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.ACTIVITY_INVENTORIES}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.REPORTING}`,
  `${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.SOCIOECONOMIC_PARAMETERS}`,
  `${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}/${EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TRANSITION_TARGETS}`,
  `${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}/${EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TARGET_BALANCE}`,
  `${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}/${EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.ENERGY_MODEL}`,
  `${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}/${EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TRANSITION_HIERARCHY}`,
  `${EDITOR_ROUTE_PATHS.INTERVENTIONS.ROOT}/${EDITOR_ROUTE_PATHS.INTERVENTIONS.CHILDREN.INTERVENTIONS}`,
  `${EDITOR_ROUTE_PATHS.INVESTMENT_PLAN}`,
  `${EDITOR_ROUTE_PATHS.DASHBOARD.ROOT}/${EDITOR_ROUTE_PATHS.DASHBOARD.CHILDREN.EMISSION_STORIES}`,
  `${EDITOR_ROUTE_PATHS.DASHBOARD.ROOT}/${EDITOR_ROUTE_PATHS.DASHBOARD.CHILDREN.TRANSITION_STORIES}`,
  `${EDITOR_ROUTE_PATHS.DASHBOARD.ROOT}/${EDITOR_ROUTE_PATHS.DASHBOARD.CHILDREN.PREVIEW.ROOT}`,
  `${APP_ROUTE_PATHS.EDITOR.ADMIN.ROOT}/${APP_ROUTE_PATHS.EDITOR.ADMIN.CHILDREN.CITY_PROGRESS}`,
];

@Injectable()
export class InventoriesEffects {
  private _actions$ = inject(Actions);
  private _store = inject(Store);
  private _injector = inject(Injector);

  /**
   * @deprecated
   */
  createValueComment = createEffect(
    () =>
      this._actions$.pipe(
        ofType(inventoriesFacadeActions.createValueComment),
        withLatestFrom(this._store.select(selectMonetaryVariableIds)),
        mergeMap(([{ commentSaveEnvelope }, monetaryVariableIds]) => {
          const backendInventoryService = this._injector.get(BackendInventoryService);

          const body = {
            boardId: commentSaveEnvelope.boardId,
            inventoryId: commentSaveEnvelope.inventoryId,
            valueId: commentSaveEnvelope.valueId,
            comment: commentSaveEnvelope.comment,
          };
          const isMonetaryValue = monetaryVariableIds.has(commentSaveEnvelope.variableNameId);
          if (isMonetaryValue) {
            return backendInventoryService.createMonetaryValueComment(body);
          }
          return backendInventoryService.createValueComment(body);
        }),
        catchError((err) => {
          throw err;
        })
      ),
    { dispatch: false }
  );

  getVariableLogs = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesFacadeActions.loadVariableLogs),
      withLatestFrom(this._store.select(selectRouteBoardId)),
      mergeMap(([params, boardId]) => {
        const { variableNameId, id, inventoryId } = params;
        const backendInventoryService = this._injector.get(BackendInventoryService);

        return backendInventoryService
          .getVariableLogs({
            boardId: boardId!,
            inventoryId,
            variableValueId: id,
            variableNameId,
          })
          .pipe(
            map((variableDetails) => {
              return inventoriesEffectsActions.loadedVariableLogs({
                valueId: id,
                variableDetails,
              });
            }),
            catchError((error) => {
              error.message = `InventoriesEffects - Error loading variable details (board id: ${boardId}: ${error.message}`;
              error.errorType = ErrorType.VARIABLES_LOAD;
              return of(inventoriesEffectsActions.loadingVariableLogsFailed({ error }));
            })
          );
      })
    )
  );

  loadInventories = createEffect(() =>
    combineLatest([
      this._actions$.pipe(ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION)),
      this._store.select(selectBoard),
      this._store.select(selectInventories),
      this._store.select(selectRouteBoardId),
    ]).pipe(
      filter(([navigationData, board, inventories, boardId]) => {
        const isBoardInSync = board?.id === boardId;
        const areInventoriesLoaded = !!inventories;
        const url = navigationData.payload.routerState.url;
        const areInventoriesRequiredForRoute = pathsRequiringInventories.some((route) => url.includes(route));

        return !!board && areInventoriesRequiredForRoute && isBoardInSync && !areInventoriesLoaded;
      }),
      exhaustMap(([navigationData, board]) => {
        const { boardId, organisationId } = navigationData.payload.routerState.params;

        if (!organisationId || !boardId) {
          return of(
            inventoriesEffectsActions.loadingInventoriesFailed({
              error: new Error('InventoriesFacadeService - Error No board or organisation id'),
            })
          );
        }

        const backendInventoryService = this._injector.get(BackendInventoryService);
        const baselineYear = board.boardSettings.timeline.scenarioStartYear;
        const estimatesLocale = board.boardSettings.estimatesLocale;
        return of(inventoriesEffectsActions.loadInventoriesStart()).pipe(
          mergeMap(() =>
            backendInventoryService.getInventories(boardId, baselineYear, estimatesLocale).pipe(
              map((inventories) => {
                return inventoriesEffectsActions.loadedInventories({ inventories });
              }),
              catchError((error) => {
                error.message = `InventoriesFacadeService - Error loading inventories (boardId: ${board.id}): ${error.message}`;
                error.errorType = error.status === 202 ? ErrorType.INVENTORIES_PROCESSING : ErrorType.INVENTORIES_LOAD;
                return of(inventoriesEffectsActions.loadingInventoriesFailed({ error }));
              })
            )
          )
        );
      })
    )
  );

  routeBoardIdChanged = createEffect(() =>
    this._actions$.pipe(
      ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION),
      map((navigationData) => navigationData.payload.routerState.params),
      filter(({ boardId, organisationId }) => !!boardId && !!organisationId),
      distinctUntilChanged((prev, curr) => prev.boardId === curr.boardId),
      map(() => {
        return inventoriesEffectsActions.loadedInventories({ inventories: null });
      })
    )
  );

  /**
   * @deprecated
   */
  saveOrQueueVariable = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesFacadeActions.saveVariable),
      withLatestFrom(this._store.select(selectVariablesSaving)),
      map(([{ variableSaveEnvelope }, variablesSaving]) => {
        const { boardId, variableNameId } = variableSaveEnvelope.requestParams;
        const queueId = createVariableSaveQueueId(boardId, variableNameId);
        const isAlreadySaving = variablesSaving.includes(queueId);
        if (isAlreadySaving) {
          return inventoriesEffectsActions.queueVariableSave({ variableSaveEnvelope });
        } else {
          return inventoriesEffectsActions.saveVariable({ variableSaveEnvelope });
        }
      })
    )
  );

  /**
   * @deprecated
   */
  saveQueuedVariable = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesEffectsActions.savedVariable),
      withLatestFrom(this._store.select(selectVariablesQueuedForSave)),
      filter(([{ variable }, variablesQueuedForSave]) => {
        return !!variablesQueuedForSave[variable.queueId];
      }),
      map(([{ variable }, variablesQueuedForSave]) => {
        const variableSaveEnvelope = variablesQueuedForSave[variable.queueId];
        return inventoriesEffectsActions.saveQueuedVariable({ variableSaveEnvelope });
      })
    )
  );

  /**
   * @deprecated
   */
  savingVariable = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesEffectsActions.saveVariable, inventoriesEffectsActions.saveQueuedVariable),
      withLatestFrom(
        this._store.select(selectInventories).pipe(filter((inventory) => !!inventory)),
        this._store.select(selectBoard).pipe(filter((board) => !!board)),
        this._store.select(selectMonetaryVariableIds)
      ),
      mergeMap(([{ variableSaveEnvelope }, inventories, board, monetaryVariableIds]) => {
        const {
          requestParams: { boardId, variableNameId, organisationId, valueMetaData, valueId },
        } = variableSaveEnvelope;
        const queueId = createVariableSaveQueueId(boardId, variableNameId);

        if (!boardId || !variableNameId || !organisationId) {
          return of(
            inventoriesEffectsActions.savingVariableFailed({
              error: new Error('Missing critical request parameters (boardId, variableNameId or organisationId)'),
              queueId,
            })
          );
        }

        let foundVariable: ClientVariable | undefined;
        for (const [_, inventory] of inventories!) {
          foundVariable = inventory.variables.find((variable) => variable.valueId === valueId);
          if (foundVariable) {
            break;
          }
        }

        if (!foundVariable) {
          return of(
            inventoriesEffectsActions.savingVariableFailed({
              error: new Error(`Variable ${variableNameId} not found in variables list`),
              queueId,
            })
          );
        }

        const isMonetaryVariable = monetaryVariableIds.has(foundVariable.name);

        const variableToSave = {
          ...foundVariable,
          ...variableSaveEnvelope.updatedProperties,
          saveId: variableSaveEnvelope.saveId,
        };

        return this._saveVariableValue$({
          board,
          variableToSave,
          queueId,
          boardId,
          variableNameId,
          valueMetaData,
          isMonetaryVariable,
        });
      })
    )
  );

  saveInventoryValue = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesFacadeActions.saveInventoryValue),
      withLatestFrom(this._store.select(selectBoard), this._store.select(selectRouteOrganisationId)),
      mergeMap(([update, board, organisationId]) => {
        if (!board || !organisationId) {
          return of(
            inventoriesEffectsActions.savingInventoryValueFailed({
              error: new Error('Missing critical request parameters (board, organisationId)'),
            })
          );
        }

        const inventorySaveManager = this._injector.get(InventorySaveManagerService);

        inventorySaveManager.queueSave(update);
        return of(inventoriesEffectsActions.savingInventory({ inventoryId: update.inventoryId }));
      })
    )
  );

  updateInventoryMonetaryValue = createEffect(() =>
    this._actions$.pipe(
      ofType(inventoriesFacadeActions.saveInventoryMonetaryValue),
      withLatestFrom(
        this._store.select(selectInventories),
        this._store.select(selectBoard),
        this._store.select(selectRouteOrganisationId)
      ),
      mergeMap(([{ inventoryId, monetaryValue }, , board, organisationId]) => {
        const backendInventoryService = this._injector.get(BackendInventoryService);

        if (!board || !inventoryId || !monetaryValue || !organisationId) {
          return of(
            inventoriesEffectsActions.savingInventoryValueFailed({
              error: new Error(
                'Missing critical request parameters (board, inventoryId, monetaryValue, organisationId)'
              ),
            })
          );
        }

        const hasMatchingInventoryId = monetaryValue.inventoryId === inventoryId;
        if (!hasMatchingInventoryId) {
          return of(
            inventoriesEffectsActions.savingInventoryValueFailed({
              error: new Error(
                `InventoriesFacadeService - Error inventory id mismatch (boardId: ${board.id}, inventoryId: ${inventoryId})`
              ),
            })
          );
        }

        return backendInventoryService
          .updateInventoryMonetaryValue({
            id: monetaryValue.id,
            boardId: board.id,
            inventoryId,
            updatedMonetaryValue: monetaryValue,
          })
          .pipe(
            map(() => inventoriesEffectsActions.savedInventoryMonetaryValue({ monetaryValue })),
            catchError((error) => {
              error.message = `InventoriesFacadeService - Error saving inventory value (boardId: ${board.id}, inventoryId: ${inventoryId} monetaryId: ${monetaryValue.id}): ${error.message}`;
              error.errorType = ErrorType.INVENTORIES_SAVE;

              return of(inventoriesEffectsActions.savingInventoryValueFailed({ error }));
            })
          );
      })
    )
  );

  throwLoadingError = createEffect(
    () =>
      this._actions$.pipe(
        ofType(inventoriesEffectsActions.loadingInventoriesFailed),
        map(({ error }) => {
          throw error;
        })
      ),
    { dispatch: false }
  );

  throwSaveError = createEffect(
    () =>
      this._actions$.pipe(
        ofType(inventoriesEffectsActions.savingInventoryValueFailed),
        map(({ error }) => {
          throw error;
        })
      ),
    { dispatch: false }
  );

  /**
   * @deprecated
   */
  private _saveVariableValue$({
    boardId,
    variableNameId,
    valueMetaData,
    board,
    queueId,
    variableToSave,
    isMonetaryVariable,
  }: {
    boardId: string;
    variableNameId: string;
    valueMetaData?: UpdateValueRequest['changeReason'] | CreateValueRequest['changeReason'];
    board?: ClientBoard;
    queueId: string;
    variableToSave: Variable & { saveId: string };
    isMonetaryVariable: boolean;
  }) {
    if (!board || !board.boardSettings?.currency || !variableToSave.inventoryId) {
      return of(
        inventoriesEffectsActions.savingVariableFailed({
          error: new Error('Missing inventory ID, board details, or currency'),
          queueId,
        })
      );
    }

    const updateValues$ = this._injector.get(MultiyearUpdateValuesService).updateValues({
      boardId,
      variableDefinitionId: variableToSave.name,
      value: variableToSave.metricValue!,
      isMonetaryVariable,
      valueMetaData,
    });

    return updateValues$.pipe(
      map(() => {
        const variableForStore = {
          ...variableToSave,
          queueId: createVariableSaveQueueId(boardId, variableNameId),
        };
        return inventoriesEffectsActions.savedVariable({ variable: variableForStore });
      }),
      catchError((error: Error & { status: number; errorType: ErrorType }) => {
        error.message = `InventoriesEffects - Error saving variable (board id: ${boardId}, variable name id: ${variableNameId}): ${error.message}`;
        error.errorType = ErrorType.VARIABLE_SAVE;

        return of(inventoriesEffectsActions.savingVariableFailed({ error, queueId }));
      })
    );
  }
}
